import { useCallback, useEffect, useState } from "react";
import { userApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { TID } from "@/lib/constants";
import { Link } from "react-router-dom";
import SignInPrompt from "@/components/SignInPrompt";
import FlipCard from "@/components/FlipCard";
import { toast } from "sonner";
import { Package, Trash2 } from "lucide-react";

export default function Saved() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const d = await userApi.listBookmarks();
      setItems(d.items || []);
    } catch (err) {
      console.error("[saved] Failed to load bookmarks:", err);
      setItems([]);
    }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      if (!user) setSignInOpen(true);
      else load();
    }
  }, [authLoading, user, load]);

  const remove = async (card_id) => {
    try {
      await userApi.removeBookmark(card_id);
      setItems((prev) => prev.filter((i) => i.card.card_id !== card_id));
      toast("Removed from Saved");
    } catch (err) {
      console.error("[saved] Remove bookmark failed:", err);
      toast.error("Failed to remove");
    }
  };

  // Bookmark toggle handler for the embedded FlipCard: only "un-bookmark" is
  // meaningful inside Saved (every card is already saved). Tapping the
  // bookmark icon removes it, mirroring the Feed's behavior.
  const onToggleBookmark = async (card) => {
    await remove(card.card_id);
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <div
        className="mx-auto w-full max-w-[760px] px-4 sm:px-6 pt-5"
        style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
      >
        <h1 className="text-lg font-semibold text-[#F8FAFC]">Saved Cards</h1>

        {/* Retention banner — new high-yield copy */}
        <div
          data-testid="saved-retention-banner"
          className="mt-3 flex items-start gap-3 rounded-2xl border border-[#F2BD68]/35 bg-gradient-to-r from-[#F2BD68]/10 via-[#F2BD68]/6 to-transparent px-4 py-3"
        >
          <Package className="h-4 w-4 mt-0.5 shrink-0 text-[#F2BD68]" aria-hidden />
          <p className="text-[12.5px] text-[#F8FAFC]" style={{ lineHeight: 1.6 }}>
            <span className="font-semibold text-[#F2BD68]">10-Day Retention Window:</span>
            {" "}Bookmarks retire after 10 days. Revise high-yield notes before they move to cold storage!
          </p>
        </div>

        {!user ? (
          <div className="mt-6 rounded-2xl border border-[#26334D] bg-[#1A2232] p-6 text-center">
            <p className="text-sm font-semibold text-[#F8FAFC]">Sign in to see saved cards.</p>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Your bookmarks live in your account.</p>
            <button
              onClick={() => setSignInOpen(true)}
              className="mt-4 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2 text-sm font-semibold hover:bg-[#C94A1D] transition-colors"
            >Sign in with Google</button>
          </div>
        ) : loading ? (
          <div className="mt-6 space-y-4">
            {[0, 1].map((k) => (
              <div key={k} className="h-72 rounded-2xl bg-[#1A2232] border border-[#26334D] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-[#26334D] bg-[#1A2232] p-6 text-center">
            <p className="text-sm font-semibold text-[#F8FAFC]">Nothing saved yet.</p>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Bookmark cards you want to revise. They retire after 10 days.</p>
            <Link
              data-testid={TID.savedEmptyGoFeed}
              to="/"
              className="mt-4 inline-block rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2 text-sm font-semibold hover:bg-[#C94A1D] transition-colors"
            >Go to Feed</Link>
          </div>
        ) : (
          <ul className="mt-5 space-y-5">
            {items.map(({ card, bookmark }) => (
              <li
                key={card.card_id}
                data-testid={TID.savedItem}
                className="space-y-2"
              >
                {/* Retention chip pinned above the card so the reader knows how much time remains */}
                <div className="flex items-center justify-between gap-2 px-1">
                  <span
                    data-testid={TID.savedPurgeIndicator}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${
                      bookmark.days_left <= 2
                        ? "border-[#E85A2A]/50 bg-[#E85A2A]/12 text-[#E85A2A]"
                        : "border-[#26334D] bg-[#111622] text-[#94A3B8]"
                    }`}
                  >
                    <Package className="h-3 w-3" aria-hidden />
                    Retires in {bookmark.days_left} {bookmark.days_left === 1 ? "day" : "days"}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(card.card_id)}
                    className="inline-flex items-center gap-1 rounded-xl border border-[#26334D] bg-[#1A2232] px-2.5 py-1 text-[11px] font-semibold text-[#94A3B8] hover:text-[#B42318] hover:border-[#B42318]/40 transition-colors"
                    data-testid={`saved-remove-${card.card_id}`}
                    aria-label="Remove from saved"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                    Remove
                  </button>
                </div>

                {/* Interactive FlipCard — same experience as the main feed. Users can tap
                    Flip to read Prelims Pointers / Diagram / Mains Utility / Full Brief. */}
                <div
                  className="relative w-full mx-auto"
                  style={{ maxWidth: 720, height: "min(72dvh, 620px)", perspective: 1200 }}
                >
                  <FlipCard
                    card={card}
                    isBookmarked={true}
                    onToggleBookmark={onToggleBookmark}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <SignInPrompt open={signInOpen} onClose={() => setSignInOpen(false)} action="see your saved cards" context={{ redirect: "/saved" }} />
    </div>
  );
}
